import type { ReactNode} from "react";
import { useEffect, useState } from "react";
import type { AgentContextValue, AgentHistoryEntry } from "@/context/agent/types.ts";
import { getAgentHistory, pruneAgentHistory, setAgentHistory } from "@/utils/agentHistoryService.ts";
import { AgentContext } from "@/context/agent/AgentContext.tsx";

export const AgentProvider = ({ children }: { children: ReactNode }) => {
  const [history, setHistory] = useState<AgentHistoryEntry[]>(() => getAgentHistory());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const prune = () => {
      setHistory(prev => {
        const next = pruneAgentHistory(prev);
        if (next.length === prev.length) return prev;
        setAgentHistory(next);
        return next;
      });
    };

    prune();
    const intervalId = window.setInterval(prune, 60_000); // 1 minute
    return () => window.clearInterval(intervalId);
  }, []);

  const appendEntry = (query?: string, response?: string) => {
    setHistory(prev => {
      const entry: AgentHistoryEntry = {
        id: `${Date.now()}-${prev.length}`,
        query,
        response,
        createdAt: new Date().toISOString(),
      };
      const next = [...prev, entry];
      setAgentHistory(next);
      return next;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    setAgentHistory([]);
  };

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(prev => !prev);

  const value: AgentContextValue = {
    history,
    appendEntry,
    clearHistory,
    isOpen,
    open,
    close,
    toggle,
  };

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};
